import React, { useEffect, useState } from "react";
import { useNavigate, Link, useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { EggmanLogo } from "../components/EggmanLogo";
import { ArrowRight, ShieldCheck, Truck, User, Eye, EyeOff, Clock, Sparkles, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const ROLE_META = {
  customer: { title: "Customer Login", blurb: "Track current orders, reorder favourites, and manage your delivery profile.", demoEmail: "", accent: "#EA580C", redirect: "/customer/dashboard", icon: User },
  driver:   { title: "Driver Login",   blurb: "View today's runs, mark deliveries, and call customers in one tap.",          demoEmail: "", accent: "#166534", redirect: "/driver/dashboard",  icon: Truck },
  admin:    { title: "Admin Login",    blurb: "Manage orders, products, drivers and customers across NZ.",                    demoEmail: "admin@eggman.co.nz", demoPw: "admin1234", accent: "#0E2818", redirect: "/admin/dashboard",   icon: ShieldCheck },
};

export default function Login({ role = "customer" }) {
  const meta = ROLE_META[role];
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const sessionExpired = params.get("reason") === "session_expired";
  const [email, setEmail] = useState(meta.demoEmail || "");
  const [password, setPassword] = useState(meta.demoPw || "");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [magicOpen, setMagicOpen] = useState(false);
  const [magicEmail, setMagicEmail] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [magicBusy, setMagicBusy] = useState(false);

  const sendMagicLink = async (e) => {
    e?.preventDefault();
    if (!magicEmail || !magicEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    setMagicBusy(true);
    try {
      await api.post("/auth/magic-link/request", { email: magicEmail, next_path: meta.redirect });
      setMagicSent(true);
      toast.success("Check your inbox — link valid for 15 minutes");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Something went wrong. Try again.");
    } finally { setMagicBusy(false); }
  };

  useEffect(() => {
    if (sessionExpired) toast.warning("Your session expired — please sign in again.");
  }, [sessionExpired]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    const res = await login(email, password, role);
    setBusy(false);
    if (!res.ok) { setErr(res.error); toast.error(res.error); return; }
    toast.success(`Welcome back, ${res.user.name}`);
    const nextParam = params.get("next");
    navigate(nextParam || location.state?.from || meta.redirect);
  };

  const Icon = meta.icon;
  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAF9]" data-testid={`login-page-${role}`}>
      <div className="p-6 md:p-10 lg:p-14 flex flex-col">
        <Link to="/"><EggmanLogo /></Link>
        <div className="flex-1 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background: `${meta.accent}1A`, color: meta.accent }}>
              <Icon size={14} />
              <span className="text-[11px] font-bold uppercase tracking-widest">{role}</span>
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl text-stone-900 tracking-tight mt-5">{meta.title}</h1>
            <p className="mt-3 text-stone-600">{meta.blurb}</p>

            {sessionExpired && (
              <div className="mt-5 flex items-start gap-2 bg-amber-50 border border-amber-200 text-amber-900 rounded-2xl px-4 py-3 text-sm" data-testid="login-session-expired">
                <Clock size={16} className="mt-0.5 shrink-0" />
                <span>Your session expired or has been invalidated. Please sign in again to continue.</span>
              </div>
            )}

            <form onSubmit={submit} className="mt-8 space-y-4" data-testid="login-form">
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Email</label>
                <input type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} data-testid="login-email-input" className="mt-2 w-full rounded-xl border border-stone-200 bg-white px-4 py-3.5 outline-none focus:border-[#166534]" />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-stone-500">Password</label>
                <div className="relative mt-2">
                  <input type={show ? "text" : "password"} required value={password} onChange={(e)=>setPassword(e.target.value)} data-testid="login-password-input" className="w-full rounded-xl border border-stone-200 bg-white px-4 py-3.5 pr-12 outline-none focus:border-[#166534]" />
                  <button type="button" onClick={()=>setShow(s=>!s)} data-testid="login-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-700 p-1" aria-label="Toggle password visibility">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
                </div>
              </div>

              {err && <div className="text-sm text-red-600 font-semibold" data-testid="login-error">{err}</div>}

              <button type="submit" disabled={busy} data-testid="login-submit-btn" className="w-full text-white rounded-full font-bold px-8 py-4 hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: meta.accent }}>
                {busy ? "Signing in…" : (<>Sign in <ArrowRight size={18} /></>)}
              </button>
            </form>

            <div className="mt-5 flex justify-between text-sm">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="font-semibold text-[#166534] hover:underline">Forgot password?</Link>
              {role === "customer" && <Link to="/signup" className="font-semibold text-stone-600 hover:text-stone-900">Create account</Link>}
              {role === "driver" && <Link to="/driver/apply" data-testid="driver-apply-link" className="font-semibold text-stone-600 hover:text-stone-900">Apply as driver</Link>}
            </div>

            {role === "customer" && (
              <>
                <div className="mt-6 flex items-center gap-3">
                  <div className="h-px bg-stone-200 flex-1" />
                  <div className="text-[10px] font-bold uppercase tracking-widest text-stone-400">or</div>
                  <div className="h-px bg-stone-200 flex-1" />
                </div>

                {/* Google Sign-In (Emergent-managed) */}
                {/* REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
                <button
                  type="button"
                  onClick={() => {
                    try { sessionStorage.setItem("post_google_redirect", meta.redirect); } catch (_e) { /* storage disabled */ }
                    const redirectUrl = window.location.origin + "/auth/callback";
                    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                  }}
                  data-testid="google-login-btn"
                  className="mt-4 w-full inline-flex items-center justify-center gap-3 rounded-full border-2 border-stone-200 hover:border-stone-400 bg-white px-6 py-3.5 font-bold text-sm text-stone-800 transition"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
                    <path fill="#4285F4" d="M17.64 9.204c0-.638-.057-1.251-.163-1.84H9v3.481h4.844a4.14 4.14 0 0 1-1.796 2.716v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z"/>
                    <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"/>
                    <path fill="#FBBC05" d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z"/>
                    <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z"/>
                  </svg>
                  Continue with Google
                </button>

                {!magicOpen ? (
                  <button
                    type="button"
                    onClick={() => setMagicOpen(true)}
                    data-testid="magic-link-open"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full border-2 border-stone-200 hover:border-[#EA580C] px-6 py-3.5 font-bold text-sm text-stone-800 transition"
                  >
                    <Sparkles size={16} className="text-[#EA580C]" />
                      Sign in with Magic Link (passwordless)
                  </button>
                ) : magicSent ? (
                  <div className="mt-4 rounded-2xl border-2 border-green-200 bg-green-50 p-4" data-testid="magic-link-sent">
                    <div className="flex items-center gap-2 text-[#166534] font-bold text-sm">
                      <Mail size={16} /> Sign-in link sent!
                    </div>
                    <div className="mt-1 text-xs text-stone-700">
                      Check <b>{magicEmail}</b> — the link is valid for 15 minutes.
                    </div>
                    <button
                      onClick={() => { setMagicSent(false); setMagicOpen(false); }}
                      className="mt-3 text-xs text-stone-500 hover:text-stone-900 font-semibold"
                    >
                      Use another email
                    </button>
                  </div>
                ) : (
                  <form onSubmit={sendMagicLink} className="mt-4 space-y-3" data-testid="magic-link-form">
                    <input
                      type="email"
                      value={magicEmail}
                      onChange={(e) => setMagicEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                      data-testid="magic-link-email-input"
                      className="w-full rounded-xl border-2 border-stone-200 bg-white px-4 py-3 outline-none focus:border-[#EA580C] text-sm"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setMagicOpen(false)}
                        className="rounded-full border-2 border-stone-200 hover:border-stone-400 px-4 py-2.5 text-xs font-bold text-stone-700"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={magicBusy}
                        data-testid="magic-link-send-btn"
                        className="flex-1 rounded-full bg-[#EA580C] text-white px-4 py-2.5 text-xs font-bold hover:brightness-110 disabled:opacity-60"
                      >
                        {magicBusy ? "Sending…" : "Send me a sign-in link"}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}

            <div className="mt-5 flex gap-3 text-xs text-stone-500">
              {role !== "customer" && <Link to="/login" className="hover:text-stone-900">Customer</Link>}
              {role !== "driver" && <Link to="/driver/login" className="hover:text-stone-900">Driver</Link>}
              {role !== "admin" && <Link to="/admin/login" className="hover:text-stone-900">Admin</Link>}
            </div>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden" style={{ background: meta.accent }}>
        <div className="absolute inset-0 grain-overlay" />
        <div className="relative h-full flex items-center justify-center p-12">
          <div className="text-white max-w-md">
            <div className="text-[11px] font-bold uppercase tracking-[0.25em] opacity-80">EGGMAN · NZ</div>
            <div className="font-display font-black text-5xl mt-3 leading-tight tracking-tight">Farm fresh,<br />delivered with care.</div>
            <p className="mt-5 opacity-90 leading-relaxed">Auckland&apos;s most trusted egg delivery — hand-graded daily, ready when you are.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
